import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api, ApiError } from "../lib/api";

interface Props { onDone: () => void }

export default function Setup({ onDone }: Props) {
  const [secret, setSecret] = useState<string>("");
  const [uri, setUri] = useState<string>("");
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ secret: string; otpauthUri: string }>("/api/auth/setup")
      .then(async (r) => {
        setSecret(r.secret);
        setUri(r.otpauthUri);
        setQrDataUrl(await QRCode.toDataURL(r.otpauthUri, { margin: 1, width: 240 }));
      })
      .catch((e: unknown) => {
        const msg = e instanceof ApiError ? e.message : "Setup error";
        setError(msg);
      });
  }, []);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<{ recoveryCodes: string[] }>("/api/auth/setup", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      setRecoveryCodes(r.recoveryCodes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not confirm");
    } finally {
      setBusy(false);
    }
  }

  if (recoveryCodes) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="card w-full max-w-md space-y-4 p-6">
          <div className="text-lg font-semibold">Save these recovery codes</div>
          <div className="text-sm text-slate-600">
            Store these somewhere safe. Each can be used once if you lose access to your authenticator app.
          </div>
          <div className="rounded-md bg-slate-50 p-3 font-mono text-sm">
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
          <button className="btn-primary w-full" onClick={onDone}>I've saved them — continue</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={confirm} className="card w-full max-w-md space-y-4 p-6">
        <div className="text-lg font-semibold">First-time setup</div>
        <ol className="space-y-3 text-sm text-slate-700">
          <li>
            <div className="font-medium">1. Scan with Google Authenticator / Authy / 1Password</div>
            {qrDataUrl
              ? <img src={qrDataUrl} alt="TOTP QR code" className="mx-auto mt-2 h-60 w-60" />
              : <div className="mt-2 text-slate-500">Generating QR…</div>}
            {secret && (
              <details className="mt-2 text-xs text-slate-500">
                <summary className="cursor-pointer">Can't scan? Enter manually</summary>
                <div className="mt-1 break-all font-mono">{secret}</div>
              </details>
            )}
          </li>
          <li>
            <div className="font-medium">2. Enter the 6-digit code to confirm</div>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric" pattern="\d{6}" maxLength={6} required
              className="mt-2 w-full text-center font-mono text-lg tracking-widest"
              placeholder="123456"
            />
          </li>
        </ol>
        {error && <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <button type="submit" disabled={busy || !secret} className="btn-primary w-full">
          {busy ? "Confirming…" : "Confirm and save"}
        </button>
      </form>
    </div>
  );
}
